import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Button, Card, Col, DatePicker, Row, Space, Spin, Statistic, Table, Tag, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { api, money, monthRangeOf, showError, useSub, ACT_STATUS_COLORS } from './shared';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const coefSource = c =>
    (c.source === 'inherited' ? ` (унаследован с ${c.period})` : '') + (c.source === 'default' ? ' (по умолчанию)' : '');

const CoefficientTag = ({ list }) => {
    if (!list?.length) return null;
    if (new Set(list.map(c => Number(c.value))).size === 1) {
        return <Tag color="blue">Коэффициент: {list[0].value}{list.length === 1 ? coefSource(list[0]) : ''}</Tag>;
    }
    return (
        <Tag color="blue">
            Коэффициенты: {list.map(c => `${dayjs(`${c.for_period}-01`).format('MMM YYYY')} — ${c.value}`).join(' · ')}
        </Tag>
    );
};

const DashboardTab = ({ onOpenProblems, onOpenWorks }) => {
    const { meta } = useSub();
    const [period, setPeriod] = useState(() => monthRangeOf(dayjs().subtract(1, 'month')));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: d } = await api.get('/dashboard', {
                params: { periodFrom: period[0].format('YYYY-MM'), periodTo: period[1].format('YYYY-MM') }
            });
            setData(d);
        } catch (error) {
            showError(error);
        } finally {
            setLoading(false);
        }
    }, [period]);

    useEffect(() => {
        load();
    }, [load]);

    const s = data?.summary || {};
    const f = data?.finance || {};
    const tile = (title, value, extra = {}) => (
        <Col xs={12} md={8} xl={4}>
            <Card size="small" hoverable={!!extra.onClick} onClick={extra.onClick}>
                <Statistic title={title} value={value} valueStyle={extra.color ? { color: extra.color } : undefined} />
            </Card>
        </Col>
    );
    const moneyTile = (title, value) => (
        <Col xs={12} md={8} xl={4}>
            <Card size="small">
                <Statistic title={title} value={money(value)} suffix="₽" />
            </Card>
        </Col>
    );

    return (
        <Spin spinning={loading}>
            <Space style={{ marginBottom: 16 }} wrap>
                <Text>Период:</Text>
                <RangePicker
                    picker="month"
                    value={period}
                    onChange={v => v?.[0] && v?.[1] && setPeriod(monthRangeOf(v))}
                    format="MMMM YYYY"
                    allowClear={false}
                    placeholder={['С месяца', 'По месяц']}
                    presets={[
                        { label: 'Прошлый месяц', value: monthRangeOf(dayjs().subtract(1, 'month')) },
                        { label: 'Последние 3 месяца', value: [dayjs().subtract(3, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
                        { label: 'Этот квартал', value: [dayjs().startOf('quarter'), dayjs().endOf('quarter')] },
                        { label: 'С начала года', value: [dayjs().startOf('year'), dayjs().endOf('month')] }
                    ]}
                />
                <Button icon={<ReloadOutlined />} onClick={load}>Обновить</Button>
                <CoefficientTag list={data?.coefficients || (data?.coefficient ? [{ ...data.coefficient, for_period: data.period }] : [])} />
            </Space>

            <Typography.Title level={5}>Работы</Typography.Title>
            <Row gutter={[12, 12]}>
                {tile('Всего работ', s.total || 0, { onClick: () => onOpenWorks?.(period, null) })}
                {tile('Подтверждено заказчиком', s.confirmed || 0)}
                {tile('Не подтверждено', s.not_confirmed || 0, { color: s.not_confirmed ? '#d48806' : undefined, onClick: () => onOpenProblems?.(period, 'not_confirmed') })}
                {tile('Ошибки / требуют уточнения', s.errors || 0, { color: s.errors ? '#cf1322' : undefined, onClick: () => onOpenProblems?.(period) })}
                {tile('Готово к акту', s.ready || 0, { color: '#389e0d', onClick: () => onOpenWorks?.(period, 'ready') })}
                {tile('Уже закрыто (в актах)', s.closed || 0, { onClick: () => onOpenWorks?.(period, 'closed') })}
                {tile('Без субподрядчика', s.by_error?.no_subcontractor || 0, { color: s.by_error?.no_subcontractor ? '#cf1322' : undefined, onClick: () => onOpenProblems?.(period, 'no_subcontractor') })}
                {tile('Сформировано актов', data?.acts?.count || 0)}
            </Row>

            <Typography.Title level={5} style={{ marginTop: 20 }}>Финансы</Typography.Title>
            <Row gutter={[12, 12]}>
                {moneyTile('Объём выполненных работ', f.volume)}
                {moneyTile('Сумма к субподряду (оценка)', f.subcontract_estimate)}
                {moneyTile('Сумма сформированных актов', f.acts_formed)}
                {moneyTile('Передано в бухгалтерию', f.sent_to_accounting)}
                {moneyTile('Подписано', f.signed)}
                {moneyTile('Оплачено', f.paid)}
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
                <Col xs={24} xl={14}>
                    <Card size="small" title="По субподрядчикам">
                        <Table
                            size="small"
                            rowKey={r => r.subcontractor_id || 0}
                            dataSource={data?.by_subcontractor || []}
                            pagination={false}
                            columns={[
                                { title: 'Субподрядчик', dataIndex: 'name' },
                                { title: 'Работ', dataIndex: 'works', align: 'right' },
                                { title: 'Готово', dataIndex: 'ready', align: 'right' },
                                { title: 'Ошибки', dataIndex: 'errors', align: 'right', render: v => (v ? <Text type="danger">{v}</Text> : 0) },
                                { title: 'Закрыто', dataIndex: 'closed', align: 'right' },
                                { title: 'Сумма заказчика', dataIndex: 'amount', align: 'right', render: money }
                            ]}
                        />
                    </Card>
                </Col>
                <Col xs={24} xl={10}>
                    <Card size="small" title="Акты по статусам">
                        <Table
                            size="small"
                            rowKey="status"
                            pagination={false}
                            dataSource={Object.entries(data?.acts?.by_status || {}).map(([status, v]) => ({ status, ...v }))}
                            columns={[
                                { title: 'Статус', dataIndex: 'status', render: v => <Tag color={ACT_STATUS_COLORS[v]}>{meta.act_statuses?.[v] || v}</Tag> },
                                { title: 'Актов', dataIndex: 'count', align: 'right' },
                                { title: 'Сумма с НДС', dataIndex: 'amount', align: 'right', render: money }
                            ]}
                        />
                    </Card>
                </Col>
            </Row>
        </Spin>
    );
};

export default DashboardTab;
